import React from "react";
import {
  Box,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";

const TOP_BAR_BLUE = "#00005f";
const SECTION_BORDER = "2px solid #e2e8f0";
const HOME_SECTION_BG = "blue.50";
const AWAY_SECTION_BG = "orange.50";
const TOTAL_SECTION_BG = "gray.50";

// Rank and Team stay frozen while the eighteen stat columns scroll. Sticky
// cells need borderCollapse: "separate", and each one has to paint its own
// background — including on hover — or the scrolling columns show through it.
const RANK_COL_WIDTH = "48px";
const TEAM_COL_WIDTH = "120px";

/**
 * The league table with home, away and total splits.
 *
 * Shared by the pool home page and the picks page, which both show a
 * three-column Current Table and offer this as the way to see the rest. One
 * component so the two cannot drift: the home/away/total grouping, the frozen
 * columns and the eighteen-column empty state are fiddly enough that a copy
 * would be fixed in one place and not the other.
 */
export default function ExpandedTableModal({ isOpen, onClose, standingsRows = [] }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="7xl"
      motionPreset="scale"
    >
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent
        bg="white"
        color="black"
        border={`3px solid ${TOP_BAR_BLUE}`}
        borderRadius="lg"
        maxW="95vw"
      >
        <ModalHeader color={TOP_BAR_BLUE}>Expanded Table Stats</ModalHeader>
        <ModalCloseButton color={TOP_BAR_BLUE} />

        <ModalBody pb={6}>
          <Box
            overflowX="auto"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            boxShadow="sm"
          >
            <Table size="sm" variant="simple" sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
              <Thead>
                <Tr>
                  <Th
                    rowSpan={2}
                    verticalAlign="middle"
                    bg={TOP_BAR_BLUE}
                    color="white"
                    position="sticky"
                    left={0}
                    zIndex={3}
                    w={RANK_COL_WIDTH}
                    minW={RANK_COL_WIDTH}
                  >
                    Rank
                  </Th>

                  <Th
                    rowSpan={2}
                    verticalAlign="middle"
                    bg={TOP_BAR_BLUE}
                    color="white"
                    borderRight={SECTION_BORDER}
                    position="sticky"
                    left={RANK_COL_WIDTH}
                    zIndex={3}
                    w={TEAM_COL_WIDTH}
                    minW={TEAM_COL_WIDTH}
                  >
                    Team
                  </Th>

                  <Th
                    colSpan={4}
                    textAlign="center"
                    bg={TOP_BAR_BLUE}
                    color="white"
                    letterSpacing="0.5px"
                  >
                    Home
                  </Th>

                  <Th
                    colSpan={4}
                    textAlign="center"
                    bg={TOP_BAR_BLUE}
                    color="white"
                    borderLeft={SECTION_BORDER}
                    letterSpacing="0.5px"
                  >
                    Away
                  </Th>

                  <Th
                    colSpan={8}
                    textAlign="center"
                    bg={TOP_BAR_BLUE}
                    color="white"
                    borderLeft={SECTION_BORDER}
                    letterSpacing="0.5px"
                  >
                    Total
                  </Th>
                </Tr>

                <Tr>
                  <Th isNumeric bg={HOME_SECTION_BG}>P</Th>
                  <Th isNumeric bg={HOME_SECTION_BG}>W</Th>
                  <Th isNumeric bg={HOME_SECTION_BG}>D</Th>
                  <Th isNumeric bg={HOME_SECTION_BG}>L</Th>

                  <Th isNumeric bg={AWAY_SECTION_BG} borderLeft={SECTION_BORDER}>P</Th>
                  <Th isNumeric bg={AWAY_SECTION_BG}>W</Th>
                  <Th isNumeric bg={AWAY_SECTION_BG}>D</Th>
                  <Th isNumeric bg={AWAY_SECTION_BG}>L</Th>

                  <Th isNumeric bg={TOTAL_SECTION_BG} borderLeft={SECTION_BORDER}>P</Th>
                  <Th isNumeric bg={TOTAL_SECTION_BG}>W</Th>
                  <Th isNumeric bg={TOTAL_SECTION_BG}>D</Th>
                  <Th isNumeric bg={TOTAL_SECTION_BG}>L</Th>
                  <Th isNumeric bg={TOTAL_SECTION_BG}>F</Th>
                  <Th isNumeric bg={TOTAL_SECTION_BG}>A</Th>
                  <Th isNumeric bg={TOTAL_SECTION_BG}>GD</Th>
                  <Th isNumeric bg={TOTAL_SECTION_BG}>Pts</Th>
                </Tr>
              </Thead>

              <Tbody>
                {standingsRows.length > 0 ? (
                  standingsRows.map((row, index) => {
                    // Must be a real theme token — an unresolvable value (e.g. "gray.25")
                    // emits invalid CSS, leaving the sticky cells transparent so the
                    // scrolling columns show through them.
                    const rowBg = index % 2 === 0 ? "white" : "gray.50";
                    return (
                    <Tr
                      key={`expanded-${row.team_id}-${row.rank}`}
                      bg={rowBg}
                      _hover={{ bg: "gray.100", "& > td.pl-sticky-col": { bg: "gray.100" } }}
                    >
                      <Td
                        className="pl-sticky-col"
                        fontWeight="semibold"
                        position="sticky"
                        left={0}
                        zIndex={2}
                        bg={rowBg}
                        w={RANK_COL_WIDTH}
                        minW={RANK_COL_WIDTH}
                      >
                        {row.rank ?? "-"}
                      </Td>

                      <Td
                        className="pl-sticky-col"
                        fontWeight="medium"
                        whiteSpace="nowrap"
                        borderRight={SECTION_BORDER}
                        position="sticky"
                        left={RANK_COL_WIDTH}
                        zIndex={2}
                        bg={rowBg}
                        w={TEAM_COL_WIDTH}
                        minW={TEAM_COL_WIDTH}
                      >
                        <HStack spacing={2} align="center">
                          {row.logo_url && (
                            <Image src={row.logo_url} boxSize="18px" objectFit="contain" flexShrink={0} />
                          )}
                          <Text as="span" display={{ base: "inline", md: "none" }}>
                            {row.short_name || row.team_name || "-"}
                          </Text>
                          <Text as="span" display={{ base: "none", md: "inline" }}>
                            {row.team_name || "-"}
                          </Text>
                        </HStack>
                      </Td>

                      <Td isNumeric bg={HOME_SECTION_BG}>{row.home_played ?? 0}</Td>
                      <Td isNumeric bg={HOME_SECTION_BG}>{row.home_wins ?? 0}</Td>
                      <Td isNumeric bg={HOME_SECTION_BG}>{row.home_draws ?? 0}</Td>
                      <Td isNumeric bg={HOME_SECTION_BG}>{row.home_losses ?? 0}</Td>

                      <Td isNumeric bg={AWAY_SECTION_BG} borderLeft={SECTION_BORDER}>
                        {row.away_played ?? 0}
                      </Td>
                      <Td isNumeric bg={AWAY_SECTION_BG}>{row.away_wins ?? 0}</Td>
                      <Td isNumeric bg={AWAY_SECTION_BG}>{row.away_draws ?? 0}</Td>
                      <Td isNumeric bg={AWAY_SECTION_BG}>{row.away_losses ?? 0}</Td>

                      <Td isNumeric bg={TOTAL_SECTION_BG} borderLeft={SECTION_BORDER}>
                        {row.played ?? 0}
                      </Td>
                      <Td isNumeric bg={TOTAL_SECTION_BG}>{row.wins ?? 0}</Td>
                      <Td isNumeric bg={TOTAL_SECTION_BG}>{row.draws ?? 0}</Td>
                      <Td isNumeric bg={TOTAL_SECTION_BG}>{row.losses ?? 0}</Td>
                      <Td isNumeric bg={TOTAL_SECTION_BG}>{row.goals_for ?? 0}</Td>
                      <Td isNumeric bg={TOTAL_SECTION_BG}>{row.goals_against ?? 0}</Td>
                      <Td isNumeric bg={TOTAL_SECTION_BG} fontWeight="semibold">
                        {row.goal_differential ?? 0}
                      </Td>
                      <Td isNumeric bg={TOTAL_SECTION_BG} fontWeight="bold">
                        {row.points ?? 0}
                      </Td>
                    </Tr>
                    );
                  })
                ) : (
                  <Tr>
                    <Td colSpan={18}>
                      <Text color="gray.600">No standings data available.</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
